import { Combobox } from "@headlessui/react";
import { XCircleIcon } from "@heroicons/react/24/solid";
import Tippy from "@tippyjs/react";
import classNames from "classnames";
import Button from "@/components/base/CodeCombobox/Button";
import CodeComboboxResults from "@/components/base/CodeCombobox/CodeComboboxResults";
import ExpansionPropertiesForm from "@/components/base/CodeCombobox/ExpansionPropertiesForm";
import useCodeCombobox from "@/components/base/CodeCombobox/useCodeCombobox";
import Card from "@/components/base/CodingCard/Card";
import { CODING_HELPERS } from "@/components/base/CodingCard/CodingCard";
import Designation from "@/components/base/CodingCard/Designation";
import RenderCode from "@/components/base/CodingCard/RenderCode";
import RenderSystem from "@/components/base/CodingCard/RenderSystem";
import SemanticAxis from "@/components/base/CodingCard/SemanticAxis";
import TopDownForm from "@/components/forms/TopDownForm";
import RadioBox from "@/components/forms/TopDownForm/RadioBox";
import { ICoding } from "@/data-models/value-models";
import { Spinner } from "@/Icons";
import React from "react";
import { Control, useController } from "react-hook-form";
import { useQuery } from "react-query";
import { DefinedValueSet } from "@/services/content/content-client";
import { createCodeLookupQuery } from "@/services/content/useCodeLookup";
import { IQuestionItem } from "../models";
import { buildQuestion } from "./buildQuestion";
import schema from "./schema";

type FieldValues = {
  code: ICoding | null;
  orientation: "horizontal" | "vertical";
  answerType: "markdown";
};

const DEFAULT_VALUES: FieldValues = {
  code: null,
  orientation: "horizontal",
  answerType: "markdown",
};

type AddQuestionPanelProps = {
  autoFocus?: boolean;
  autoFocusRef?: React.RefObject<HTMLInputElement>;
  onSubmit?: (question: IQuestionItem) => void;
  layout?: "horizontal" | "vertical";
  className?: string;
};

function AddQuestionPanel({
  onSubmit,
  className,
  layout,
  autoFocus,
  autoFocusRef,
}: AddQuestionPanelProps) {
  const handleSubmit = (values: FieldValues) => {
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      console.log(parsed.error);
      return;
    }
    onSubmit?.(buildQuestion(parsed.data));
  };
  return (
    <TopDownForm
      className={classNames("flex flex-col gap-y-8", className)}
      onSubmit={handleSubmit}
      defaultValues={DEFAULT_VALUES}
    >
      {({ register }) => (
        <>
          <section
            className={classNames(
              "grid min-h-min grid-cols-1 gap-x-8 2xl:grid-cols-1",
              { " lg:grid-cols-3": layout == "horizontal" },
            )}
          >
            <div className="col-span-1">
              <h4 className="text-base font-semibold leading-7 text-gray-darker">
                Kies een nieuwe vraag
              </h4>
              <p className="text-sm leading-6 text-gray-primary">
                Zoek een gecodeerd concept uit de lijst of voeg zelf een concept
                toe.
              </p>
            </div>
            <QuestionComboField
              autoFocus={autoFocus}
              autoFocusRef={autoFocusRef}
              className="col-span-1 min-h-min overflow-y-scroll md:col-span-2 2xl:col-span-1"
            />
          </section>
          <section
            className={classNames(
              "grid shrink-0 grid-cols-1 gap-x-8 2xl:grid-cols-1",
              { " lg:grid-cols-3": layout == "horizontal" },
            )}
          >
            <div className="col-span-1">
              <h4 className="text-base font-semibold leading-7 text-gray-darker">
                Bepaal de antwoordmogelijkheden
              </h4>
              <p className="text-sm leading-6 text-gray-primary">
                Configureer hier hoe de bovenstaande vraag beantwoord kan
                worden.
              </p>
            </div>
            <TopDownForm.Option
              {...register("answerType")}
              id="add-question-answer-type-markdown"
              value="markdown"
              label="tekstvak"
              Component={React.forwardRef(RadioBox)}
              small
            />
          </section>
          <section
            className={classNames(
              "grid shrink-0 grid-cols-1 gap-x-8 2xl:grid-cols-1",
              { " lg:grid-cols-3": layout == "horizontal" },
            )}
          >
            <div className="cols-span-1">
              <h4 className="text-base font-semibold leading-7 text-gray-darker">
                Configureer lay-out
              </h4>
              <p className="text-sm leading-6 text-gray-primary">
                Bepaal de lay-out van de vraag en antwoordmogelijkheden.
              </p>
            </div>
            <fieldset className="mt-2">
              <legend className="text-sm font-medium text-gray-dark">
                Oriëntatie
              </legend>
              <TopDownForm.Option
                {...register("orientation")}
                id="add-question-orientation-horizontal"
                value="horizontal"
                label="horizontaal"
                Component={React.forwardRef(RadioBox)}
                small
              />
              <TopDownForm.Option
                {...register("orientation")}
                id="add-question-orientation-vertical"
                value="vertical"
                label="verticaal"
                Component={React.forwardRef(RadioBox)}
                small
              />
            </fieldset>
          </section>
          <section
            className={classNames(
              "grid shrink-0 grid-cols-1 gap-x-8 2xl:grid-cols-1",
              { " lg:grid-cols-3": layout == "horizontal" },
            )}
          >
            <div className="col-start-2 col-end-3">
              <TopDownForm.Submit className="mt-2">
                Voeg vraag toe
              </TopDownForm.Submit>
            </div>
          </section>
        </>
      )}
    </TopDownForm>
  );
}

export default AddQuestionPanel;

const QUESTION_VS: DefinedValueSet = {
  resourceType: "ValueSet",
  status: "active",
  url: "urn:uuid:ca82dfa1-e6ec-4c19-b948-4332ee45f0ef",
  compose: {
    include: [
      { system: "http://snomed.info/sct" },
      { system: "http://tiro.health/fhir/CodeSystem/auto-generated" },
    ],
  },
};
type QuestionComboFieldProps = {
  count?: number;
  control?: Control<FieldValues>;
  className?: string;
  autoFocus?: boolean;
  autoFocusRef?: React.RefObject<HTMLInputElement>;
};
function QuestionComboField({
  count,
  className,
  autoFocus,
  autoFocusRef,
  control,
}: QuestionComboFieldProps) {
  const { field, fieldState } = useController<FieldValues, "code">({
    name: "code",
    control,
    rules: {
      required: true,
    },
  });
  const {
    query,
    filteredCodes,
    handleQueryChange,
    by,
    displayValue,
    handleCodeChange,
    fetchStatus,
    helpers,
    expansionProps,
    setExpansionProps,
  } = useCodeCombobox(QUESTION_VS, {
    count,
    allowedPlacements: ["bottom-start", "bottom-end"],
  });
  return (
    <Combobox
      value={field.value}
      onChange={handleCodeChange(field.onChange)}
      by={by}
    >
      {({ open, value }) => (
        <div
          className={classNames(
            "flex max-w-md flex-col items-stretch pt-2",
            className,
          )}
        >
          {value ? (
            <div className="relative">
              <CodingCard coding={value} />
              <Tippy content="Verwijder deze code">
                <button
                  type="button"
                  onClick={() => field.onChange(null)}
                  aria-details="Remove code"
                  className="absolute -right-2 -top-2 flex cursor-pointer items-center justify-center"
                >
                  <XCircleIcon className="h-5 w-5 rounded-full border border-white bg-white text-gray-300 group-hover:text-gray-500" />
                </button>
              </Tippy>
            </div>
          ) : (
            <div className="mt-1 flex gap-x-1">
              <div className="relative grow rounded-md shadow-sm">
                <Combobox.Input
                  ref={autoFocusRef}
                  autoFocus={autoFocus}
                  autoComplete="off"
                  placeholder="Zoek een vraag"
                  className={classNames(
                    "w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-12 text-gray-900 shadow-sm ring-inset sm:text-sm sm:leading-6",
                    {
                      "focus:ring-blue-600": open,
                      "ring-1 ring-gray-300 focus:ring-2 focus:ring-blue-600":
                        !fieldState.error,
                      "ring-2 ring-red-600": fieldState.error,
                    },
                  )}
                  value={query}
                  onChange={handleQueryChange}
                  displayValue={displayValue}
                />
                <Combobox.Button
                  as={Button}
                  loading={fetchStatus.isFetching}
                  error={fetchStatus.isError}
                  success={fetchStatus.isSuccess}
                />
              </div>
              <ExpansionPropertiesForm
                value={expansionProps}
                onChange={(newProps) =>
                  setExpansionProps((oldProps) => ({
                    ...oldProps,
                    ...newProps,
                  }))
                }
              />
            </div>
          )}
          <CodeComboboxResults
            open={open && query.trim().length > 0}
            codes={filteredCodes}
            helpers={helpers}
            className="grow overflow-y-scroll"
          />
        </div>
      )}
    </Combobox>
  );
}

type CodingCardProps = {
  coding: ICoding;
};
function CodingCard({ coding }: CodingCardProps) {
  const { code, system, display } = coding;
  const { data: lookupResult, isLoading } = useQuery({
    ...createCodeLookupQuery({ code, system }),
    staleTime: 1000 * 10,
    placeholderData: {
      display,
      code,
      system,
      designation: [
        {
          value: display,
        },
      ],
    },
  });
  const semantic = CODING_HELPERS.getSemantic(lookupResult);
  const designation = CODING_HELPERS.getDesignation(display, lookupResult) ?? {
    value: display,
  };
  return (
    <Card>
      <Card.Header>
        <Designation className="grow overflow-x-clip">
          <Designation.Display designation={designation} />
        </Designation>
        {isLoading ? (
          <div>
            <Spinner className="inline h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : semantic ? (
          <div>
            <SemanticAxis {...semantic} />
          </div>
        ) : /** ) : onSemanticChange ? (
            <AddSemanticAxis onAdd={onSemanticChange} />**/
        null}
      </Card.Header>
      <Card.BodyRow>
        <div>
          <RenderSystem
            url={system}
            className="truncate whitespace-nowrap font-semibold"
          />
        </div>
        <div>
          <RenderCode code={code} />
        </div>
        <div className="shrink truncate">{display}</div>
        <div>(standard)</div>
      </Card.BodyRow>
    </Card>
  );
}
